import React, { FC } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Breadcrumbs, Link, Typography } from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { GLOBALSETTINGS } from "../../../data/constants";

/** Kept in step with PostInterviewForm's own LIST_URL — the trail's "back to the grid" hop. */
const LIST_URL = "/supervision/postif";
const LIST_LABEL = "Post Interview Lists";

interface PifBreadcrumbsProps {
  /**
   * The final, non-navigable crumb: where the user is right now. Omit it on the list page itself —
   * "Post Interview Lists" then becomes the terminal crumb instead of a link to where you already are.
   */
  current?: string;
  /**
   * "onDark" for the list page, whose header is a full-bleed brown banner — the default inherited
   * colour is unreadable on it.
   */
  tone?: "default" | "onDark";
}

/**
 * Breadcrumb trail for the PostIF screens (list, form, approval review).
 *
 * Deliberately NOT `components/form/MyBreadcrumbs`, even though that is the app's breadcrumb
 * component: it renders the Redux `breadcrumbs` slice, which is the File Explorer's *folder* trail
 * (written by FolderTree / GlobalSearchBox, keyed by folder id) and is only mounted by PageDetail
 * on the /listDetails route. Driving it from here would mean writing these routes' crumbs into that
 * shared slice and leaving them behind for the file explorer to render later. This trail is fixed
 * and needs no store, so it stays presentational.
 *
 * It also has to live inside each screen rather than in the shared page-detail band, because the
 * shell skips that band for every /supervision/postif route (App.tsx's `hidePageDetail`) — these
 * screens bring their own headers.
 */
const PifBreadcrumbs: FC<PifBreadcrumbsProps> = ({ current, tone = "default" }) => {
  const onDark = tone === "onDark";
  const linkColor = onDark ? "rgba(255, 255, 255, 0.75)" : "inherit";
  const currentColor = onDark ? "#fff" : "text.primary";

  return (
    <Box sx={{ pt: onDark ? 0 : 2, pb: onDark ? 1 : 0, fontSize: 13, color: linkColor }}>
      <Breadcrumbs
        aria-label="breadcrumbs"
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ fontSize: "inherit", "& .MuiBreadcrumbs-separator": { color: linkColor } }}
      >
        <Link
          component={RouterLink}
          to={GLOBALSETTINGS.DEFAULT_PAGE}
          underline="hover"
          sx={{ color: linkColor }}
        >
          Home
        </Link>

        {current ? (
          <Link component={RouterLink} to={LIST_URL} underline="hover" sx={{ color: linkColor }}>
            {LIST_LABEL}
          </Link>
        ) : (
          <Typography sx={{ fontSize: "inherit", fontWeight: 600, color: currentColor }}>
            {LIST_LABEL}
          </Typography>
        )}

        {current && (
          <Typography sx={{ fontSize: "inherit", fontWeight: 600, color: currentColor }}>
            {current}
          </Typography>
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default PifBreadcrumbs;
